/**
 * O cliente HTTP dos seis servicos.
 *
 * Toda chamada passa pelo gateway em `/api/{servico}/...` (AD-25: quem compoe a tela e
 * o SPA, chamando cada servico dono). O JWT viaja em cookie `HttpOnly` emitido pela
 * `identidade` — nunca em localStorage —, por isso `credentials: 'include'`.
 *
 * **Renovacao automatica no 401.** O JWT dura 15 minutos (AD-5); a sessao de renovacao,
 * horas. Sem isto, toda tela quebraria a cada quinze minutos de trabalho. A regra e
 * estreita de proposito: uma unica tentativa de renovacao por chamada, e se ela tambem
 * falhar sobe o **401 original** — o erro que o usuario precisa ver e "sua sessao acabou",
 * nao o detalhe da renovacao. Renovacoes simultaneas de varias chamadas compartilham a
 * mesma promessa: N telas tomando 401 ao mesmo tempo geram **uma** renovacao, nao N (e N
 * renovacoes rotacionariam o token N vezes, invalidando umas as outras).
 *
 * **O `FormData` entra na 8.4**, e ele passa pelo mesmo caminho de todo o resto. O upload de midia
 * precisa de uma coisa que ate aqui nao existia — mandar `multipart/form-data` **sem** escrever o
 * `Content-Type`, porque quem o monta (com o `boundary`) e o navegador —, e ela foi acrescentada a
 * este cliente em vez de virar um `fetch` solto na tela. A razao e de sempre: uma copia com a
 * renovacao no `401` reescrita divergiria desta no primeiro ajuste, e o upload de um arquivo grande
 * e justamente onde o acesso tem mais chance de expirar no meio.
 *
 * **O que o SPA nao faz e ler corpo binario por aqui.** A midia e buscada pelo proprio
 * `<img>`/`<audio>`/`<video>`, direto da URL da rota: com cache do navegador, faixa de bytes no
 * video e nada passando pela memoria do SPA.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO, ehEnvelopeDeErro } from './erros'
import type { components as EsquemasDoCadastro } from './tipos-gerados/cadastro'
import type { components as EsquemasDaComunicacao } from './tipos-gerados/comunicacao'
import type { components as EsquemasDaIdentidade } from './tipos-gerados/identidade'
import type { components as EsquemasDaIntegracaoErp } from './tipos-gerados/integracao-erp'
import type { components as EsquemasDosNegocios } from './tipos-gerados/negocios'
import type { components as EsquemasDosRelatorios } from './tipos-gerados/relatorios'

/** Lista fechada dos servicos (AD-1). */
export const SERVICOS = [
  'identidade',
  'cadastro',
  'negocios',
  'comunicacao',
  'integracao-erp',
  'relatorios',
] as const

export type Servico = (typeof SERVICOS)[number]

export interface OpcoesDaChamada extends Omit<RequestInit, 'body'> {
  /** Corpo serializado como JSON. */
  corpo?: unknown
  /**
   * Corpo `multipart/form-data` — o upload de midia da 8.4.
   *
   * **Ele e um campo proprio, e nao um `corpo` que aceitasse `FormData`**, porque o cabecalho muda:
   * com JSON o cliente escreve `Content-Type: application/json`; com `FormData` ele **nao pode
   * escrever nada**. Quem monta o `Content-Type` de um multipart e o navegador, e so ele: o valor
   * carrega o `boundary` que separa as partes, e um cabecalho escrito a mao produziria um corpo que
   * o servidor nao consegue separar — `422` sem explicacao, e so em produção.
   *
   * Os dois nunca vao juntos: `corpo` e o argumento de um ato em JSON, `formulario` e o de um ato
   * com arquivo.
   */
  formulario?: FormData
  /**
   * Nao tentar renovar a sessao no 401. As proprias rotas de sessao usam isto: renovar
   * uma renovacao recusada seria recursao, e um login errado nao e sessao expirada.
   */
  semRenovacao?: boolean
}

export function montarUrl(servico: Servico, caminho: string): string {
  const caminhoNormalizado = caminho.startsWith('/') ? caminho : `/${caminho}`
  return `/api/${servico}${caminhoNormalizado}`
}

async function lerEnvelope(resposta: Response): Promise<ErroDaApi> {
  try {
    const corpo: unknown = await resposta.json()
    if (ehEnvelopeDeErro(corpo)) {
      // O que vier alem de `codigo` e `mensagem` (13.6: o `uso` da etiqueta em uso) vai em `extras`.
      const { codigo, mensagem, ...extras } = corpo.erro as Record<string, unknown> & {
        codigo: string
        mensagem: string
      }
      return new ErroDaApi(resposta.status, codigo, mensagem, extras)
    }
  } catch {
    // Resposta sem JSON: cai na mensagem generica.
  }
  return new ErroDaApi(resposta.status, 'erro_desconhecido', MENSAGEM_DE_ERRO_DESCONHECIDO)
}

/**
 * Caminho da renovacao, na `identidade`.
 *
 * Ele mora aqui, e nao em `modulos/identidade/api.ts`, porque `chamar` precisa dele e o
 * modulo importa `chamar` — a constante no outro sentido fecharia o ciclo. O modulo o
 * importa daqui, de modo que a rota tenha **uma** definicao: escrita nos dois arquivos,
 * ela sobreviveria a uma mudanca de caminho em um so, e o sintoma seria a sessao parando
 * de renovar sozinha, com todo teste verde.
 */
export const CAMINHO_DA_RENOVACAO = '/sessoes/renovacao'

/** Renovacao em curso, compartilhada por todas as chamadas que tomaram 401 juntas. */
let renovacaoEmVoo: Promise<unknown> | null = null

function renovarSessao(): Promise<unknown> {
  renovacaoEmVoo ??= chamar<unknown>('identidade', CAMINHO_DA_RENOVACAO, {
    method: 'POST',
    semRenovacao: true,
  }).finally(() => {
    renovacaoEmVoo = null
  })
  return renovacaoEmVoo
}

/**
 * O corpo desserializado **e** o status com que ele veio.
 *
 * Serve a rota que responde `2xx` de mais de um jeito e em que a diferenca importa para a
 * tela — hoje o `POST /opcoes-de-dominio`, que devolve `201` ao criar um valor de lista e
 * `200` ao **reativar** um que fora excluido. Sem o status, os dois desfechos chegam
 * indistinguiveis e a tela confirma "cadastrado" onde restaurou.
 */
export interface RespostaDaChamada<T> {
  corpo: T
  status: number
}

/**
 * Como `chamar`, mas devolvendo tambem o status da resposta.
 *
 * As duas sao a **mesma** implementacao (`chamar` delega para esta) de proposito: uma copia
 * com a renovacao no `401` reescrita divergiria dela no primeiro ajuste, e o sintoma seria uma
 * tela que para de renovar a sessao sozinha enquanto todas as outras continuam.
 */
export async function chamarComStatus<T>(
  servico: Servico,
  caminho: string,
  opcoes: OpcoesDaChamada = {},
): Promise<RespostaDaChamada<T>> {
  const { resposta, status } = await pedir(servico, caminho, opcoes)

  if (status === 204) {
    return { corpo: undefined as T, status }
  }

  return { corpo: (await resposta.json()) as T, status }
}


/**
 * A requisicao em si: renovacao no `401`, envelope no erro, e a `Response` **crua** no sucesso.
 *
 * Ela e uma funcao separada de `chamarComStatus` porque as duas responsabilidades sao de niveis
 * diferentes — **fazer a requisicao** (com a renovacao compartilhada e o envelope) e **ler o corpo
 * como JSON** —, e so a segunda e especifica de um formato. A separacao e o que deixa o dia em que
 * o SPA precisar do corpo cru (um download que passe pelo cliente, um `text/csv`) ser uma funcao
 * de tres linhas em vez de uma segunda copia da renovacao no `401` — que e o remendo que divergiria
 * no primeiro ajuste, com o sintoma "esta tela para de renovar a sessao sozinha".
 *
 * **Hoje ela tem um chamador so**, e isso esta certo: a midia da 8.4 nao passa por aqui. Os
 * elementos `<img>`/`<audio>`/`<video>` apontam **direto** para a URL da rota, e quem busca os bytes
 * e o proprio navegador — com cache, faixa de bytes no video e nada atravessando a memoria do SPA.
 */
async function pedir(
  servico: Servico,
  caminho: string,
  opcoes: OpcoesDaChamada = {},
): Promise<{ resposta: Response; status: number }> {
  const { corpo, formulario, headers, semRenovacao = false, ...resto } = opcoes

  const enviar = () =>
    fetch(montarUrl(servico, caminho), {
      ...resto,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        // **Com `FormData` nenhum `Content-Type` e escrito**, e a ausencia e obrigatoria: quem o
        // monta e o navegador, porque o valor carrega o `boundary` que separa as partes. Escrito a
        // mao, o servidor receberia um corpo que nao consegue separar.
        ...(corpo === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
      ...(formulario === undefined ? {} : { body: formulario }),
    })

  let resposta = await enviar()

  if (resposta.status === 401 && !semRenovacao) {
    const erroOriginal = await lerEnvelope(resposta)
    try {
      await renovarSessao()
    } catch {
      // Nao ha sessao para renovar: quem sobe e o 401 da chamada original.
      throw erroOriginal
    }
    resposta = await enviar()
  }

  if (!resposta.ok) {
    throw await lerEnvelope(resposta)
  }

  return { resposta, status: resposta.status }
}

/** Chama um servico pelo gateway e devolve o corpo ja desserializado. */
export async function chamar<T>(
  servico: Servico,
  caminho: string,
  opcoes: OpcoesDaChamada = {},
): Promise<T> {
  return (await chamarComStatus<T>(servico, caminho, opcoes)).corpo
}

/**
 * O corpo de `/health`, gerado do OpenAPI de cada servico (AD-16).
 *
 * Escrito a mao, este tipo ja estava **mais estreito** que o contrato servido: faltava-lhe o
 * `pendentes_no_outbox` que os seis anunciam, e nada falhava. E exatamente o DW-17, e por isso
 * ele agora e gerado como todos os outros.
 *
 * O router de saude e do `compartilhado`, entao os seis publicam o mesmo schema. A uniao
 * abaixo e o que **prova** isso a cada compilacao: cada ramo passa pelo `ComOMesmoHealth`, que
 * so aceita um servico cujo `/health` seja compativel com o da `identidade`, e a uniao de seis
 * tipos iguais colapsa no proprio tipo. Um servico que mudasse o corpo de `/health` sozinho
 * quebraria o build aqui — em vez de o SPA seguir tipando a resposta dele pelo corpo de outro.
 */
export type RespostaDeSaude =
  | ComOMesmoHealth<EsquemasDaIdentidade>
  | ComOMesmoHealth<EsquemasDoCadastro>
  | ComOMesmoHealth<EsquemasDosNegocios>
  | ComOMesmoHealth<EsquemasDaComunicacao>
  | ComOMesmoHealth<EsquemasDaIntegracaoErp>
  | ComOMesmoHealth<EsquemasDosRelatorios>

type CorpoDeSaudeDaIdentidade = EsquemasDaIdentidade['schemas']['RespostaDeSaude']

/** O `/health` deste servico, desde que compativel com o da `identidade`. */
type ComOMesmoHealth<
  Esquemas extends { schemas: { RespostaDeSaude: CorpoDeSaudeDaIdentidade } },
> = Esquemas['schemas']['RespostaDeSaude']

/** Saude de um servico — publico, e por isso nunca dispara renovacao. */
export function consultarSaude(servico: Servico): Promise<RespostaDeSaude> {
  return chamar<RespostaDeSaude>(servico, '/health', { semRenovacao: true })
}
